import { LinkedinFilled, QuestionCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Image,
  Input,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd';
import { Helmet } from 'react-helmet-async';
import NumberFormat from 'react-number-format';
import { useQuery, useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import indeedLogo from '../../assets/indeed.png';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import { withRouteChange } from '../../services/APM';

interface IntegrationsFormValues {
  linkedinIntegration: boolean;
  linkedinIndustryCode?: string;
  linkedinCompanyId?: string;
  linkedinPosterEmail?: string;
}

const Integrations = () => {
  const [form] = Form.useForm();
  const linkedinIntegrationValue = Form.useWatch('linkedinIntegration', form);
  const queryClient = useQueryClient();
  const config = useAPIQuery('/configs/tenant-configurations/');
  const linkedinIndustryCodesV2_ptbr = useQuery<{ id: number; label: string; hierarchy: string }[]>(
    ['linkeinIndustryCodesV2'],
    {
      async queryFn() {
        const data = await fetch('/linkedin_industry_codes_v2_ptbr_1_0_0.min.json');
        return data.json();
      },
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }
  );

  const showCompanyCodeInfoModal = () => {
    Modal.info({
      content: (
        <Typography.Text>
          Informar o código identificador da empresa que pode ser encontrado de acordo com as
          instruções deste{' '}
          <a href="https://www.linkedin.com/help/linkedin/answer/a415420/associating-your-linkedin-company-id-with-the-linkedin-job-board-frequently-asked-questions?lang=pt">
            link
          </a>
          .
        </Typography.Text>
      ),
    });
  };

  const showIndustryCodeInfoModal = () => {
    Modal.info({
      width: 800,
      content: (
        <>
          <Typography.Text>
            Informar o código de indústria de acordo com a tabela abaixo:
          </Typography.Text>
          <br />
          <br />
          <Table rowKey="id" dataSource={linkedinIndustryCodesV2_ptbr.data || []}>
            <Table.Column dataIndex="id" title="Código" />
            <Table.Column dataIndex="label" title="Descrição" />
            <Table.Column dataIndex="hierarchy" title="Hierarquia" />
          </Table>
        </>
      ),
    });
  };

  const mutation = useAPIMutation(
    async (
      {
        linkedinIntegration,
        linkedinCompanyId,
        linkedinIndustryCode,
        linkedinPosterEmail,
      }: IntegrationsFormValues,
      api
    ) => {
      await api.patch('/configs/tenant-configurations/enable-linkedin-integration/', {
        value: linkedinIntegration,
      });
      await api.patch('/configs/tenant-configurations/linkedin-company-id/', {
        value: parseInt(linkedinCompanyId || '0', 10),
      });
      await api.patch('/configs/tenant-configurations/linkedin-industry-code/', {
        value: parseInt(linkedinIndustryCode || '0', 10),
      });
      await api.patch('/configs/tenant-configurations/linkedin-poster-email/', {
        value: linkedinPosterEmail || '',
      });
    },
    {
      onSuccess: () => {
        message.success('Configurações atualizadas.');
      },
      onSettled: () => {
        queryClient.invalidateQueries('/configs/tenant-configurations/');
      },
    }
  );

  return (
    <ErrorResult error={config.error}>
      <Helmet>
        <title>Divulgações | Integrações | Configurações | Recrutamento e Seleção</title>
      </Helmet>
      <Spin spinning={config.isFetching}>
        <Form<IntegrationsFormValues>
          form={form}
          key={`${config.isSuccess}`}
          layout="vertical"
          initialValues={{
            linkedinIntegration: Boolean(config.data?.enableLinkedinIntegration),
            linkedinIndustryCode: config.data?.linkedinIndustryCode,
            linkedinCompanyId: config.data?.linkedinCompanyId,
            linkedinPosterEmail: config.data?.linkedinPosterEmail,
          }}
          onFinish={(values) => {
            mutation.mutate(values);
          }}
        >
          <Form.Legend>Canais de divulgação de vagas:</Form.Legend>
          <Typography.Paragraph>
            Permite que suas vagas sejam automaticamente publicadas nos job boards abaixo.
          </Typography.Paragraph>
          <Space direction="vertical">
            <Card>
              <Form.Row align="middle">
                <Form.Col span={4} style={{ display: 'flex', justifyContent: 'center' }}>
                  <LinkedinFilled style={{ fontSize: 64, color: '#0E76A8' }} />
                </Form.Col>
                <Form.Col span={20}>
                  <Form.Legend>Linkedin</Form.Legend>
                  <Form.Item name="linkedinIntegration" valuePropName="checked">
                    <Checkbox>Ativado</Checkbox>
                  </Form.Item>
                  <Form.Row>
                    <Form.Col span={12}>
                      <Form.Item
                        label="Código da empresa"
                        name="linkedinCompanyId"
                        tooltip={{
                          icon: (
                            <QuestionCircleOutlined
                              style={{ color: '#1890ff' }}
                              onClick={() => showCompanyCodeInfoModal()}
                            />
                          ),
                        }}
                        rules={[{ required: linkedinIntegrationValue }]}
                      >
                        <NumberFormat disabled={!linkedinIntegrationValue} customInput={Input} />
                      </Form.Item>
                    </Form.Col>
                    <Form.Col span={12}>
                      <Form.Item
                        label="Código de indústria"
                        name="linkedinIndustryCode"
                        tooltip={{
                          icon: (
                            <QuestionCircleOutlined
                              style={{ color: '#1890ff' }}
                              onClick={() => showIndustryCodeInfoModal()}
                            />
                          ),
                        }}
                        rules={[{ required: linkedinIntegrationValue }]}
                      >
                        <Select
                          showSearch
                          disabled={!linkedinIntegrationValue}
                          optionFilterProp="children"
                        >
                          {(linkedinIndustryCodesV2_ptbr.data || []).map(({ id, label }) => (
                            <Select.Option key={id} value={id}>
                              {id} - {label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Form.Col>
                    <Form.Col span={12}>
                      <Form.Item
                        label="E-mail associado a conta no Linkedin da empresa"
                        name="linkedinPosterEmail"
                        rules={[{ required: linkedinIntegrationValue }, { type: 'email' }]}
                      >
                        <Input />
                      </Form.Item>
                    </Form.Col>
                  </Form.Row>
                </Form.Col>
              </Form.Row>
            </Card>
            <Card>
              <Form.Row align="middle">
                <Form.Col span={4} style={{ display: 'flex', justifyContent: 'center' }}>
                  <Image src={indeedLogo} preview={false} />
                </Form.Col>
                <Form.Col flex={1}>
                  <Form.Legend>Indeed</Form.Legend>
                  {config.data?.enableIndeedIntegration ? (
                    <>
                      <Form.Item>
                        <Checkbox checked disabled>
                          Ativado
                        </Checkbox>
                      </Form.Item>
                      <Typography.Paragraph>
                        Para desabilitar essa integração é necessário entrar em contato com o Indeed
                      </Typography.Paragraph>
                    </>
                  ) : (
                    <Typography.Paragraph>
                      Para habilitar essa integração é necessário entrar em contato com o Indeed
                    </Typography.Paragraph>
                  )}
                  <Typography.Paragraph>
                    Solicite ajuda ao CS responsável pela conta
                  </Typography.Paragraph>
                </Form.Col>
              </Form.Row>
            </Card>
            <Form.Footer>
              <Form.FooterEnd>
                <Button type="primary" loading={mutation.isLoading} htmlType="submit">
                  Salvar alterações
                </Button>
              </Form.FooterEnd>
            </Form.Footer>
          </Space>
        </Form>
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(Integrations);
